import { useNavigate, useParams } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Screen } from "@/components/screen";
import { Button, Card, Field, SelectField } from "@/components/ui";
import { useToast } from "@/contexts";
import type { BadgeDefinitionResponse } from "@/features/badges/api";
import { getAllBadgeDefinitions, getCompetitionBadgePool } from "@/features/badges/api";
import {
  deleteCompetition,
  getCompetitionById,
  updateCompetitionSettings,
} from "@/features/competitions/api";
import { COMPETITION_BADGE_COUNT, pickTieredBadges } from "@/server/data/badges";
import type { CompetitionSummary } from "@/types";

type BadgeMode = "auto" | "manual";

function toggleBadge(current: string[], badgeId: string): string[] {
  if (current.includes(badgeId)) return current.filter((id) => id !== badgeId);
  if (current.length >= COMPETITION_BADGE_COUNT) return current;
  return [...current, badgeId];
}

export default function CompetitionSettings() {
  const { id } = useParams({ from: "/competition/$id/settings" }) as { id: string };
  const navigate = useNavigate();
  const [competition, setCompetition] = useState<CompetitionSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [visibility, setVisibility] = useState<"invite-only" | "discoverable">("invite-only");
  const [badgeMode, setBadgeMode] = useState<BadgeMode>("manual");
  const [allBadges, setAllBadges] = useState<BadgeDefinitionResponse[]>([]);
  const [autoBadgeIds, setAutoBadgeIds] = useState<string[]>([]);
  const [selectedBadgeIds, setSelectedBadgeIds] = useState<string[]>([]);
  const [existingBadgePool, setExistingBadgePool] = useState<string[]>([]);
  const toast = useToast();

  useEffect(() => {
    if (!id) return;

    (async () => {
      try {
        const [{ data }, poolResult, badgeDefResult] = await Promise.all([
          getCompetitionById(id),
          getCompetitionBadgePool(id),
          getAllBadgeDefinitions(),
        ]);
        if (!data) return;

        setCompetition(data);
        setName(data.name);
        setDescription(data.description ?? "");
        setVisibility(data.visibility ?? "invite-only");

        const badges = badgeDefResult.data ?? [];
        const sorted = badges.sort((a, b) => a.stepThreshold - b.stepThreshold);
        setAllBadges(sorted);

        if (poolResult.data && poolResult.data.length > 0) {
          const ids = poolResult.data.map((b) => b.badgeId);
          setExistingBadgePool(ids);
          setSelectedBadgeIds(ids);
          setAutoBadgeIds(ids);
        } else {
          setAutoBadgeIds(
            pickTieredBadges(
              sorted.map((b) => ({
                id: b.badgeId,
                name: b.name,
                description: b.description,
                iconEmoji: b.iconEmoji,
                stepThreshold: b.stepThreshold,
                tier: b.tier,
              })),
            ).map((b) => b.id),
          );
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (!competition) {
    return (
      <Screen title="Settings" eyebrow="Competition" backTo="/dashboard">
        {loading && <div />}
      </Screen>
    );
  }

  const isAdmin = competition.role === "owner" || competition.role === "admin";
  const canEditBadges = competition.startsAt > new Date().toISOString().slice(0, 10);

  function handleShuffle() {
    setAutoBadgeIds(
      pickTieredBadges(
        allBadges.map((b) => ({
          id: b.badgeId,
          name: b.name,
          description: b.description,
          iconEmoji: b.iconEmoji,
          stepThreshold: b.stepThreshold,
          tier: b.tier,
        })),
      ).map((b) => b.id),
    );
  }

  async function handleSave() {
    if (!id) return;
    const badgeIds = badgeMode === "auto" ? autoBadgeIds : selectedBadgeIds;
    const { error } = await updateCompetitionSettings(id, {
      name: name.trim() || undefined,
      description: description.trim() || undefined,
      visibility,
      badgeIds: canEditBadges ? badgeIds : undefined,
    });
    if (error) {
      toast.showAlert("Error", error.message, "error");
    } else {
      toast.showAlert("Saved", "Competition settings updated", "success");
      navigate({ to: ".", replace: true });
    }
  }

  async function handleDelete() {
    toast.showConfirm(
      "Delete competition",
      "This will permanently remove the competition. This action cannot be undone.",
      async () => {
        const { error } = await deleteCompetition(id);
        if (error) toast.showAlert("Error", error.message, "error");
        else navigate({ to: "/dashboard", replace: true });
      },
    );
  }

  const autoBadges: BadgeDefinitionResponse[] = autoBadgeIds
    .map((bid) => allBadges.find((b) => b.badgeId === bid))
    .filter((b): b is BadgeDefinitionResponse => b != null);

  const sortedAllBadges = [...allBadges].sort((a, b) => a.stepThreshold - b.stepThreshold);

  return (
    <Screen
      backTo="/dashboard"
      title="Competition settings"
      eyebrow={competition.name}
      subtitle="Edit the competition name, description, or visibility"
    >
      <div className="flex flex-col gap-3.5 pb-10">
        <Card tone="strong">
          <Field label="Competition name" value={name} onChangeText={setName} />
          <Field label="Description" value={description} onChangeText={setDescription} multiline />
          <SelectField
            label="Visibility"
            value={visibility}
            options={[
              { label: "Invite-only", value: "invite-only" },
              { label: "Discoverable", value: "discoverable" },
            ]}
            onValueChange={(v) => setVisibility(v as "invite-only" | "discoverable")}
          />
        </Card>

        {canEditBadges ? (
          <Card tone="strong">
            <span className="text-text-muted text-[0.625rem] font-bold uppercase tracking-[0.05em]">
              Badges
            </span>
            <p className="text-text-muted text-xs mt-0.5">
              Choose which badges will be available in this competition.
            </p>

            <div className="flex flex-row bg-[rgba(0,0,0,0.04)] rounded-2xl p-0.5 gap-0">
              <button
                type="button"
                onClick={() => setBadgeMode("auto")}
                className={`flex-1 py-2 text-center rounded-xl cursor-pointer font-inherit border-0 ${
                  badgeMode === "auto" ? "bg-aqua" : ""
                }`}
              >
                <span
                  className={`text-xs font-semibold ${badgeMode === "auto" ? "text-white" : "text-text-muted"}`}
                >
                  Pick for me
                </span>
              </button>
              <button
                type="button"
                onClick={() => setBadgeMode("manual")}
                className={`flex-1 py-2 text-center rounded-xl cursor-pointer font-inherit border-0 ${
                  badgeMode === "manual" ? "bg-aqua" : ""
                }`}
              >
                <span
                  className={`text-xs font-semibold ${badgeMode === "manual" ? "text-white" : "text-text-muted"}`}
                >
                  Choose my own
                </span>
              </button>
            </div>

            {badgeMode === "auto" ? (
              <div className="flex flex-col gap-2.5">
                {autoBadges.map((badge) => (
                  <div
                    key={badge.badgeId}
                    className="flex flex-row items-center gap-2.5 bg-white rounded-xl p-2.5 border border-[rgba(28,27,46,0.1)]"
                  >
                    <span className="text-2xl">{badge.iconEmoji}</span>
                    <div className="flex-1">
                      <span className="text-text text-xs font-semibold">{badge.name}</span>
                      <p className="text-text-muted text-[0.6875rem] mt-0.5">
                        {badge.stepThreshold.toLocaleString()} steps — {badge.description}
                      </p>
                    </div>
                  </div>
                ))}
                <Button label="Shuffle badges" kind="secondary" onPress={handleShuffle} />
              </div>
            ) : selectedBadgeIds.length > 0 ? (
              <div className="flex flex-col gap-2">
                <p className="text-text-muted text-xs font-semibold text-center">
                  {selectedBadgeIds.length} / {COMPETITION_BADGE_COUNT} selected
                </p>
                {sortedAllBadges.map((badge) => {
                  const isSelected = selectedBadgeIds.includes(badge.badgeId);
                  const isFull = selectedBadgeIds.length >= COMPETITION_BADGE_COUNT && !isSelected;
                  return (
                    <button
                      type="button"
                      key={badge.badgeId}
                      onClick={() =>
                        setSelectedBadgeIds(toggleBadge(selectedBadgeIds, badge.badgeId))
                      }
                      disabled={isFull}
                      className={`flex flex-row items-center gap-2.5 bg-white rounded-xl p-2.5 border cursor-pointer font-inherit text-left ${
                        isSelected
                          ? "border-aqua bg-[rgba(255,45,126,0.04)]"
                          : "border-[rgba(28,27,46,0.1)]"
                      } ${isFull ? "opacity-40" : ""}`}
                    >
                      <div
                        className={`w-[1.375rem] h-[1.375rem] rounded border-2 flex items-center justify-center shrink-0 ${
                          isSelected ? "bg-aqua border-aqua" : "border-text-muted"
                        }`}
                      >
                        {isSelected && <span className="text-white text-xs font-extrabold">✓</span>}
                      </div>
                      <span className="text-2xl">{badge.iconEmoji}</span>
                      <div className="flex-1">
                        <span className="text-text text-xs font-semibold">{badge.name}</span>
                        <p className="text-text-muted text-[0.6875rem] mt-0.5">
                          {badge.stepThreshold.toLocaleString()} steps — {badge.description}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {sortedAllBadges.map((badge) => {
                  const isSelected = selectedBadgeIds.includes(badge.badgeId);
                  const isFull = selectedBadgeIds.length >= COMPETITION_BADGE_COUNT && !isSelected;
                  return (
                    <button
                      type="button"
                      key={badge.badgeId}
                      onClick={() =>
                        setSelectedBadgeIds(toggleBadge(selectedBadgeIds, badge.badgeId))
                      }
                      disabled={isFull}
                      className={`flex flex-row items-center gap-2.5 bg-white rounded-xl p-2.5 border cursor-pointer font-inherit text-left ${
                        isSelected
                          ? "border-aqua bg-[rgba(255,45,126,0.04)]"
                          : "border-[rgba(28,27,46,0.1)]"
                      } ${isFull ? "opacity-40" : ""}`}
                    >
                      <div
                        className={`w-[1.375rem] h-[1.375rem] rounded border-2 flex items-center justify-center shrink-0 ${
                          isSelected ? "bg-aqua border-aqua" : "border-text-muted"
                        }`}
                      >
                        {isSelected && <span className="text-white text-xs font-extrabold">✓</span>}
                      </div>
                      <span className="text-2xl">{badge.iconEmoji}</span>
                      <div className="flex-1">
                        <span className="text-text text-xs font-semibold">{badge.name}</span>
                        <p className="text-text-muted text-[0.6875rem] mt-0.5">
                          {badge.stepThreshold.toLocaleString()} steps — {badge.description}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </Card>
        ) : (
          <Card>
            <span className="text-text-muted text-[0.625rem] font-bold uppercase tracking-[0.05em]">
              Badges
            </span>
            <p className="text-text-muted text-xs mt-0.5">
              Badge pool is locked once the competition starts.
            </p>
            {existingBadgePool.length > 0 ? (
              <div className="flex flex-col gap-2">
                {existingBadgePool
                  .map((bid) => allBadges.find((b) => b.badgeId === bid))
                  .filter((b): b is BadgeDefinitionResponse => b != null)
                  .map((badge) => (
                    <div
                      key={badge.badgeId}
                      className="flex flex-row items-center gap-2.5 bg-white rounded-xl p-2.5 border border-[rgba(28,27,46,0.1)]"
                    >
                      <span className="text-2xl">{badge.iconEmoji}</span>
                      <div className="flex-1">
                        <span className="text-text text-xs font-semibold">{badge.name}</span>
                        <p className="text-text-muted text-[0.6875rem] mt-0.5">
                          {badge.stepThreshold.toLocaleString()} steps — {badge.description}
                        </p>
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <p className="text-text-muted text-[0.6875rem] mt-2">
                No badge pool defined for this competition.
              </p>
            )}
          </Card>
        )}

        <Button label="Save changes" onPress={handleSave} />

        {isAdmin && (
          <Card>
            <span className="text-text-muted text-[0.625rem] font-bold uppercase tracking-[0.05em]">
              Danger Zone
            </span>
            <button
              type="button"
              onClick={handleDelete}
              className="w-full text-center py-2.5 rounded-lg bg-[rgba(255,59,48,0.1)] cursor-pointer border-0 font-inherit"
            >
              <span className="text-coral text-sm font-bold">Delete competition</span>
            </button>
          </Card>
        )}
      </div>
    </Screen>
  );
}
